import React, { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { leaseCapFullDraftHint } from '../utils/leasePetCapMessages';

interface PetCountProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: (count: number) => void;
  maxPetsAllowed?: number; // From admin settings - the total property limit
  remainingSlots?: number; // How many more pets the user can register
  propertyName?: string;
  /** Saved drafts on the resident's dashboard (each consumes a lease slot). */
  draftCount?: number;
}

const PetCount: React.FC<PetCountProps> = ({
  isOpen,
  onClose,
  onContinue,
  maxPetsAllowed = 5, // Default to 5 if not provided
  remainingSlots,
  propertyName,
  draftCount = 0,
}) => {
  const [selectedCount, setSelectedCount] = useState<number>(1);

  // Use remainingSlots if provided, otherwise fall back to maxPetsAllowed
  const slotsAvailable = remainingSlots !== undefined ? remainingSlots : maxPetsAllowed;

  // Clamp when the modal reopens or lease summary loads — stale selectedCount
  // (e.g. user picked 2 earlier, now only 1 slot left) must not pass through.
  useEffect(() => {
    if (!isOpen || slotsAvailable <= 0) return;
    setSelectedCount((prev) => Math.min(prev, slotsAvailable));
  }, [isOpen, slotsAvailable]);

  if (!isOpen) return null;

  // Generate pet count options based on remaining slots
  const petCountOptions = slotsAvailable > 0
    ? Array.from({ length: slotsAvailable }, (_, i) => i + 1)
    : [];

  const handleContinue = () => {
    const count = slotsAvailable > 0 ? Math.min(selectedCount, slotsAvailable) : selectedCount;
    onContinue(count);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="relative max-w-md w-full">
        {/* Outer container with gray background and border */}
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-2 shadow-2xl">
          {/* Inner white container */}
          <div className="bg-white rounded-lg overflow-hidden">
            
            {/* Header with icon, title and close button */}
            <div className="flex items-center justify-between p-4 pb-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 text-blue-600">
                  <Info className="w-full h-full" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">
                  How Many Pets do you have?
                </h2>
              </div>
            </div>

            {/* Alert section */}
            <div className="px-4 pb-4 space-y-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
                <div className="w-5 h-5 text-blue-600 flex-shrink-0">
                  <Info className="w-full h-full" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-blue-900">
                    You must register every pet
                  </div>
                </div>
              </div>

              {/* Show max pets limit set by admin */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-3">
                <div className="w-5 h-5 text-purple-600 flex-shrink-0">
                  <Info className="w-full h-full" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-purple-900">
                    {propertyName ? `${propertyName} allows` : 'This property allows'} a maximum of <span className="font-semibold">{maxPetsAllowed}</span> pet{maxPetsAllowed > 1 ? 's' : ''} per unit
                  </div>
                  {remainingSlots !== undefined && remainingSlots < maxPetsAllowed && remainingSlots > 0 && (
                    <div className="text-sm text-purple-700 mt-1">
                      You can register <span className="font-semibold">{remainingSlots}</span> more pet{remainingSlots > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {slotsAvailable > 0 ? (
              <>
                {/* Number selection section - touch-friendly buttons */}
                <div className="px-5 pb-4">
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-900">
                      Number of Pets
                    </label>
                    <div className="bg-white border border-gray-300 rounded-lg p-1">
                      <div className="flex items-center gap-1">
                        {petCountOptions.map((number) => (
                          <button
                            key={number}
                            onClick={() => setSelectedCount(number)}
                            className={`flex-1 min-h-[44px] py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                              selectedCount === number
                                ? 'bg-purple-600 text-white'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {number}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action buttons - touch-friendly */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 p-4 pt-2">
                  <button
                    onClick={handleCancel}
                    className="min-h-[44px] px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-full text-xs font-medium uppercase tracking-wide hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleContinue}
                    className="min-h-[44px] px-4 py-2.5 bg-[color:var(--pmc-primary)] text-white rounded-full text-xs font-bold uppercase tracking-wide hover:shadow-lg transition-all"
                  >
                    Continue
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* No slots remaining */}
                <div className="px-4 pb-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-3">
                    <div className="w-5 h-5 text-red-600 flex-shrink-0">
                      <Info className="w-full h-full" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="text-sm font-semibold text-red-900">
                        You&apos;ve reached the maximum number of pets allowed for this unit
                      </div>
                      <div className="text-sm text-red-800">
                        {leaseCapFullDraftHint(draftCount)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end p-4 pt-2">
                  <button
                    onClick={handleCancel}
                    className="min-h-[44px] px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-full text-xs font-medium uppercase tracking-wide hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetCount;
